import { toConversationError } from './errors';
import type { OutgoingFile } from './thread';

import { createDownloadUrlV2, type DownloadUrlPurpose } from '@/services/createDownloadUrlV2';
import { createUploadUrlV2 } from '@/services/createUploadUrlV2';
import { uploadFileToPresignedUrl } from '@/services/uploadFileToPresignedUrl';
import { getFileContent } from '@/util/getFileContent';

/**
 * Uploads a picked file's bytes and returns the key a message names it by.
 * Failures come back as a `ConversationError`, so a dropped connection is
 * `network` and the send that called this retries it like the send itself.
 */
export async function uploadSupportFile(file: OutgoingFile): Promise<string> {
  try {
    const upload = await createUploadUrlV2({ mimeType: file.mimeType });

    if (!upload?.url || !upload.s3Key) {
      throw new Error('No upload URL returned');
    }

    const content = await getFileContent(file);

    await uploadFileToPresignedUrl({ presignedUrl: upload.url, file: content });

    return upload.s3Key;
  } catch (error) {
    throw toConversationError(error);
  }
}

/**
 * A short-lived URL for a file in a support thread. The server answers only
 * the thread's participants, and the same not-found for everyone else.
 *
 * `purpose` is required, so a caller states whether it is drawing a picture,
 * opening a preview or saving a file. The server reports the last two to staff
 * and never the first, which is how a thread full of thumbnails stays quiet.
 */
export async function getSupportFileUrl(
  s3Key: string,
  purpose: DownloadUrlPurpose
): Promise<string> {
  const data = await createDownloadUrlV2(s3Key, purpose);

  if (!data?.url) {
    throw new Error('No download URL returned');
  }

  return data.url;
}
